import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import SectorsFilter from '../SectorsFilter/SectorsFilter';
import DivisionFilter from '../DivisionFilter/DivisionFilter';
import CoResponsibleDivisionFilter from '../CoResponsibleDivisionFilter/CoResponsibleDivisionFilter';
import ParticipatingDivisionFilter from '../ParticipatingDivisionFilter/ParticipatingDivisionFilter';
import { CalledFrom } from '../../../models/CalledFromEnum';
import { AppMenuTabs } from '../../MenuTabs/userTabsInterface';
import SelectOption from '../../../models/SelectOption';
import { getDivisionFilterOptions, manageSectorsAndDivisionsFilterBehavior } from '../UtilFunctions';

type Props = {
  calledFrom: CalledFrom;
  isOpen: boolean;
  isEdit?: boolean;
  setSectorSelectedOnParent?: Function;
  setDivisionSelectedOnParent?: Function;
  setCoResponsibleDivisionOnParent?: Function;
  setParticipantDivisionOnParent?: Function;
};

const getPadding = showHiddenFilters => {
  return showHiddenFilters ? { paddingBottom: '25px' } : { paddingBottom: '10px' };
};

const handleFilterChange = (value: SelectOption[], setOptionsSelectedOnParent: any, setSectorsSelected: any = null) => {
  setOptionsSelectedOnParent && setOptionsSelectedOnParent(value);
  setSectorsSelected && setSectorsSelected(value);
};

const SectorsAndDivisionsFilter = (props: Props) => {
  const { t } = useTranslation();
  const {
    calledFrom,
    isOpen,
    isEdit,
    setSectorSelectedOnParent,
    setDivisionSelectedOnParent,
    setCoResponsibleDivisionOnParent,
    setParticipantDivisionOnParent,
  } = props;

  const menuTabs = useSelector<any, AppMenuTabs>(state => state.menuTabs);
  const filterIndicators = useSelector<any, any>(state => state.filterIndicators);
  const filterProjects = useSelector<any, any>(state => state.filter);
  const filterEarlyWarnings = useSelector<any, any>(state => state.filterEarlyWarnings);
  const sectorsFromRedux = useSelector<any, any>(state => state.sectors);
  const divisionsFromRedux = useSelector<any, any>(state => state.divisions);

  const [showSectorFilter, setShowSectorFilter] = useState<boolean>(true);
  const [showDivisionFilters, setShowDivisionFilters] = useState<boolean>(true);
  const [showHiddenFilters, setShowHiddenFilters] = useState<boolean>(false);
  const [sectorsSelected, setSectorsSelected] = useState<SelectOption[]>([]);
  const [availableDivisions, setAvailableDivisions] = useState<SelectOption[]>([]);

  useEffect(() => {
    manageSectorsAndDivisionsFilterBehavior(
      isOpen,
      calledFrom,
      menuTabs,
      { filterIndicators, filterProjects, filterEarlyWarnings },
      { setShowHiddenFilters, setShowSectorFilter, setShowDivisionFilters },
      { sectorsFromRedux, divisionsFromRedux },
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    if (sectorsSelected && sectorsSelected.length) {
      const filteredSectors = sectorsFromRedux.filter(sector =>
        sectorsSelected.map(selected => selected.id).includes(sector.id),
      );
      const divisionsFromSelected = filteredSectors.flatMap(filtered => filtered.divisions);
      const divisionsOptions = getDivisionFilterOptions(divisionsFromSelected);
      setAvailableDivisions(divisionsOptions);
    } else {
      const divisionsOptions = getDivisionFilterOptions(divisionsFromRedux);
      setAvailableDivisions(divisionsOptions);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectorsSelected]);

  return (
    <>
      {showSectorFilter && (
        <>
          <h4>{t('section')}</h4>
          <div className="horizontal-columns">
            <SectorsFilter
              calledFrom={calledFrom}
              setSelectedSectorsOnParent={value =>
                handleFilterChange(value, setSectorSelectedOnParent, setSectorsSelected)
              }
              isEdit={isEdit}
            />
          </div>
        </>
      )}
      {showDivisionFilters && (
        <>
          <h4>{t('division_capital')}</h4>
          <div className="horizontal-columns" style={getPadding(showHiddenFilters)}>
            <DivisionFilter
              calledFrom={calledFrom}
              divisionOptionsFromParent={availableDivisions}
              setSelectedDivisionOnParent={value => handleFilterChange(value, setDivisionSelectedOnParent)}
              isEdit={isEdit}
            />
          </div>
          {!showHiddenFilters ? (
            <div className="hidden-filters-text" onClick={() => setShowHiddenFilters(true)}>
              {t('double_booking_and_collaboration')}
            </div>
          ) : (
            <>
              <h4>{t('co_responsible_division')}</h4>
              <div className="horizontal-columns">
                <CoResponsibleDivisionFilter
                  calledFrom={calledFrom}
                  divisionOptionsFromParent={availableDivisions}
                  setCoResponsibleDivisionOnParent={value =>
                    handleFilterChange(value, setCoResponsibleDivisionOnParent)
                  }
                  isEdit={isEdit}
                />
              </div>
              <h4>{t('participating_division')}</h4>
              <div className="horizontal-columns">
                <ParticipatingDivisionFilter
                  calledFrom={calledFrom}
                  divisionOptionsFromParent={availableDivisions}
                  setParticipantDivisionOnParent={value => handleFilterChange(value, setParticipantDivisionOnParent)}
                  isEdit={isEdit}
                />
              </div>
            </>
          )}
        </>
      )}
    </>
  );
};

export default SectorsAndDivisionsFilter;
